import "./Step.scss";
import React, { useState, useEffect } from "react";

import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Container, Grid } from "@material-ui/core";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { Alert, AlertTitle } from "@material-ui/lab";

import { SectionHeader } from "../../atoms/SectionHeader";
import { CircularLoader } from "../../atoms/Loaders";
import Stepper from "../../atoms/Stepper";
import CustomTabs from "../../organisms/CustomTabs";
import { useDispatch, useSelector } from "react-redux";
import { get_specific_programme_step } from "../../../store/actions/getProgrammeStep";
import useUserId from "../../hooks/useUserId";
import useCompletedSteps from "../../hooks/useCompletedSteps";
import slugConverter from "../../../utils/SlugConverter";
import JourneyStates from "../../../enums/journeyStates";
import { BackButtonLink } from "../../atoms/Button";
import { UserProgrammeOverview } from "../../../types/UserProgrammeOverview";
import { useCurrentProgrammeOverview } from "../../hooks/useCurrentProgrammeOverview";
import { useCurrentJourney } from "../../hooks/useCurrentJourney";
import { useGetProfileByIdQuery } from "../../../redux/profileApiV3";
import { IsObjectEmptyOrNull } from "../../../utils/IsEmpty";

interface Props {
  programmeOverview: UserProgrammeOverview | undefined;
}

const Step: React.FC<Props> = () => {
  const { programmeSlug, stepNumber } = useParams();
  const content = useSelector((state: any) => state.programmeStep);
  const [currentStep, setCurrentStep] = useState<any>({});
  const dispatch = useDispatch();
  const [userId] = useUserId();
  const profileByIdResponse = useGetProfileByIdQuery(userId ?? skipToken);
  const programmeOverview = useCurrentProgrammeOverview(
    profileByIdResponse,
    programmeSlug
  );
  const currentJourney = useCurrentJourney(profileByIdResponse, programmeSlug);

  const completedSteps = useCompletedSteps(programmeOverview?.steps);

  useEffect(() => {
    if (
      !IsObjectEmptyOrNull(currentJourney) &&
      !IsObjectEmptyOrNull(programmeOverview)
    ) {
      programmeOverview.loading !== true &&
        dispatch(
          get_specific_programme_step(
            userId,
            programmeOverview.programme.id,
            currentJourney.id,
            parseInt(stepNumber)
          )
        );
    }
  }, [dispatch, currentJourney, programmeOverview, userId]);

  useEffect(() => {
    if (Object.keys(content.data).length > 0) {
      setCurrentStep(content.data);
    }
  }, [content.data]);

  return (
    <div className="main-page step-page">
      {content.data &&
      content.loading === false &&
      !IsObjectEmptyOrNull(currentStep) &&
      !IsObjectEmptyOrNull(currentJourney) ? (
        <>
          <Helmet>
            <title>
              {`${currentStep.title} | ${slugConverter.toTitleCase(
                currentStep.programmeSlug
              )} |  My Path | Gloji`}
            </title>
          </Helmet>
          <Container className="main-content__container">
            <section>
              <div className="header">
                {currentJourney.state !== JourneyStates.PENDING && (
                  <div className="back-button">
                    <BackButtonLink
                      linkPath={`/my-path/${programmeSlug}`}
                      btnText="My path"
                      ariaLabel="Back to My path"
                    />
                  </div>
                )}
                <div className="header__inner">
                  <div className="header__title">
                    {programmeOverview?.programme.title}
                  </div>
                </div>
              </div>
              {currentStep.stepNumber !== 0 && (
                <SectionHeader>
                  {programmeOverview?.programme !== undefined && (
                    <Stepper
                      totalSteps={programmeOverview.programme.numberOfSteps - 1}
                      completedSteps={completedSteps}
                      currentStep={parseInt(stepNumber)}
                    />
                  )}
                </SectionHeader>
              )}
              <div className="parts-and-sections ">
                {content.errorStatus ? (
                  <>
                    <Grid container>
                      <Grid item xs={12}>
                        <div style={{ height: "60vh", paddingTop: "4rem" }}>
                          <Alert severity="error">
                            <AlertTitle>{content.error.message} </AlertTitle>
                            Failed to load data
                          </Alert>
                        </div>
                      </Grid>
                    </Grid>
                  </>
                ) : (
                  Object.keys(currentStep).length && (
                    <CustomTabs
                      stepData={currentStep ? currentStep : []}
                      programmeOverview={programmeOverview}
                    />
                  )
                )}
              </div>
            </section>
          </Container>
        </>
      ) : (
        <CircularLoader color="primary" size={40} />
      )}
    </div>
  );
};

export default Step;
